import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromAiAgent, setFromAiAgent] = useState(false);
  // Local districtAdmin/counselor role + district, resolved from the backend's
  // AccessAssignment table (the external JWT only ever carries 'superAdmin').
  const [access, setAccess] = useState(null);

  const refreshAccess = useCallback(async () => {
    try {
      const response = await authAPI.getMyAccess();
      setAccess(response.data);
    } catch (error) {
      console.error('Error fetching access assignment:', error);
      setAccess(null);
    }
  }, []);

  // Load user from localStorage on mount and listen for SSO token from AI Agent Dashboard
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');
    const storedFromAi = localStorage.getItem('fromAiAgent') === 'true';

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setFromAiAgent(storedFromAi);
      setLoading(false);
    } else {
      setLoading(false);
    }

    const messageHandler = (event) => {
      // Only accept messages from AI Agent Dashboard origin (from env)
      const allowedOrigin = process.env.REACT_APP_AI_AGENT_DASHBOARD_URL;
      if (!allowedOrigin || event.origin !== allowedOrigin) {
        return;
      }

      const incomingToken = event.data?.token;
      const incomingUser = event.data?.user;

      if (!incomingToken || typeof incomingToken !== 'string' || !incomingUser) {
        return;
      }

      localStorage.setItem('authToken', incomingToken);
      localStorage.setItem('authUser', JSON.stringify(incomingUser));
      localStorage.setItem('fromAiAgent', 'true');

      setToken(incomingToken);
      setUser(incomingUser);
      setFromAiAgent(true);

      if (window.location.pathname === '/login') {
        window.location.replace('/');
      }
     
    };

    window.addEventListener('message', messageHandler);

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, []);

  // Resolve districtAdmin/counselor access whenever a token becomes available
  // (initial load from localStorage, SSO message, or a fresh login) and clear
  // it on logout.
  useEffect(() => {
    if (token) {
      refreshAccess();
    } else {
      setAccess(null);
    }
  }, [token, refreshAccess]);

  // Login function
  const login = async (email, password) => {
    try {
      const response = await axios.post('https://agentadmin.glcredentials.com/api/auth/login', {
        email,
        password
      });

      if (response.data.status === 'success' && response.data.response) {
        const { token: authToken, user: userData } = response.data.response;

        // Store token and user data
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('authUser', JSON.stringify(userData));

        setToken(authToken);
        setUser(userData);

        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.message || 'Login failed'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed. Please try again.'
      };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('fromAiAgent');
    setToken(null);
    setUser(null);
    setFromAiAgent(false);
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const isSuperAdmin = () => user?.role === 'superAdmin';
  const isDistrictAdmin = () => access?.access?.role === 'districtAdmin';
  const isCounselor = () => access?.access?.role === 'counselor';

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated,
    fromAiAgent,
    access: access?.access || null,
    refreshAccess,
    isSuperAdmin,
    isDistrictAdmin,
    isCounselor
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
